import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/core';
import type { CommentAnchor, ComposeInput, Draft, GuestInfo, ID, Story, TextFrameItem } from '@proofroom/schema';
import { resolveFace } from '@proofroom/schema';
import { Header } from '../components/Header';
import { Tour } from '../components/Tour';
import { GUEST_TOUR, VIEWER_TOUR } from '../components/tourSteps';
import { useTour } from '../hooks/useTour';
import { EmptyState, ErrorState, Spinner } from '../components/ui';
import { ApiError } from '../api/client';
import { keys, useDocument, useSnapshot, useThreads } from '../api/queries';
import { documents as docApi, drafts as draftApi, threads as threadApi, type SendChangesResult } from '../api/endpoints';
import { useServerEvents } from '../api/events';
import { indexSnapshot, pageAtPoint, storyFrames } from '../model/snapshotIndex';
import { storyGeometry } from '../model/frameGeometry';
import { baseParagraphs, materialise, storiesDiffer } from '../model/materialise';
import { oversetInfo, type OversetInfo } from '../model/overset';
import { resolveParaAttrs } from '../model/attrs';
import { fontLocks, isAvailableFace } from '../model/fontLock';
import type { PMNode } from '../model/pmJson';
import { differingStories } from '../print/sheet';
import { creativeOptionGroups, chooseOption, type LayerOverrides } from '../render/layers';
import { fitScale, fitWidthScale } from '../render/scale';
import { flushFidelityLog } from '../render/fidelity';
import { domMeasure } from '../render/measure';
import { SpreadSvg } from '../render/SpreadSvg';
import { registerSnapshotFaces, type FaceLoadResult } from '../fonts/faces';
import { useCompose } from '../engine/useCompose';
import { RoomRegistry } from '../editor/rooms';
import type { SelectionInfo } from '../editor/selection';
import { useViewer } from '../state/viewerStore';
import { useSession } from '../auth/session';
import { FIXTURE_MODE } from '../mock/install';
import { Navigator } from '../viewer/Navigator';
import { Toolbar } from '../viewer/Toolbar';
import { GuestHeader } from '../viewer/GuestHeader';
import { PreviewDialog, type PreviewScope } from '../viewer/PreviewDialog';
import { EditorPanel } from '../viewer/EditorPanel';
import { ThreadsPanel } from '../viewer/ThreadsPanel';
import { ChangesPanel } from '../viewer/ChangesPanel';
import { SendResultBanner } from '../viewer/SendResultBanner';
import { PublishBanner } from '../viewer/PublishBanner';
import { ConnectionBanner } from '../viewer/ConnectionBanner';
import { useViewportSize } from '../viewer/useViewportSize';

/** Retention deleted this draft's files (server: 410 with code 'pruned'), which is not a fault to retry. */
const wasPruned = (err: unknown): boolean => err instanceof ApiError && err.status === 410 && err.code === 'pruned';

export function DraftViewer() {
  const { documentId = '', draftId = '' } = useParams();
  const doc = useDocument(documentId);
  const snap = useSnapshot(draftId);
  if (doc.isPending || snap.isPending) return <Shell title="Loading"><Spinner label={snap.isPending ? 'Loading the snapshot' : 'Loading the document'} /></Shell>;
  if (doc.isError) return <Shell title="Error"><ErrorState title="Could not load the document" error={doc.error} retry={() => void doc.refetch()} /></Shell>;
  const draft = doc.data.drafts.find((d) => d.id === draftId);
  if (!draft) return <Shell title={doc.data.name}><ErrorState title="This draft does not belong to the document" /></Shell>;
  if (snap.isError && wasPruned(snap.error)) {
    const current = doc.data.drafts.find((d) => d.id === doc.data.currentDraftId);
    return (
      <Shell title={doc.data.name}>
        <EmptyState title={`Draft ${draft.number}'s files were pruned`}>
          Its snapshot and images were deleted to reclaim space. The draft, its changes and its comments are all still here; only the proof is gone.
          {current && <> The current draft is {current.number}. <Link to={`/documents/${documentId}/drafts/${current.id}`}>Open draft {current.number}</Link></>}
        </EmptyState>
      </Shell>
    );
  }
  if (snap.isError) return <Shell title="Error"><ErrorState title="Could not load this draft" error={snap.error} retry={() => void snap.refetch()} /></Shell>;
  // swatch and paint hexes arrive colour managed from InDesign's own app.colorTransform
  return <Viewer key={draftId} documentId={documentId} draft={draft} drafts={doc.data.drafts} docName={doc.data.name} projectId={doc.data.projectId} snapshotData={snap.data} />;
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="app"><Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: title }]} /><main className="main">{children}</main></div>;
}

/** How often the guest viewer asks again for the document (a new draft moves the link) and its threads, while the tab is visible. */
export const GUEST_POLL_MS = 30_000;

/**
 * The viewer a share link opens: always the document's current draft, whichever that is now,
 * with the editing, deciding and publishing surfaces gone. No SSE for guests, so the document
 * and the threads are polled instead (see the Viewer's guest effect).
 */
export function GuestDraftViewer({ guest, onLeave }: { guest: GuestInfo; onLeave(): void }) {
  const documentId = guest.documentId;
  const doc = useDocument(documentId);
  const draftId = doc.data?.currentDraftId ?? '';
  const snap = useQuery({ queryKey: keys.snapshot(draftId), queryFn: () => draftApi.snapshot(draftId), enabled: draftId !== '', staleTime: Infinity, gcTime: 10 * 60 * 1000 });
  const shell = (children: React.ReactNode) => (
    <div className="app"><GuestHeader docName={doc.data?.name ?? 'Proof'} guestName={guest.name} onLeave={onLeave} /><main className="main">{children}</main></div>
  );
  if (doc.isPending) return shell(<Spinner label="Loading the document" />);
  if (doc.isError) return shell(<ErrorState title="Could not load the document" error={doc.error} retry={() => void doc.refetch()} />);
  const draft = doc.data.drafts.find((d) => d.id === draftId);
  if (!draftId || !draft) return shell(<EmptyState title="No draft yet">The designer has not published a draft of this document. Check back later.</EmptyState>);
  if (snap.isPending) return shell(<Spinner label="Loading the snapshot" />);
  if (snap.isError) return shell(<ErrorState title="Could not load this draft" error={snap.error} retry={() => void snap.refetch()} />);
  return <Viewer key={draftId} mode="guest" documentId={documentId} draft={draft} drafts={[draft]} docName={doc.data.name} projectId={doc.data.projectId} snapshotData={snap.data} onLeave={onLeave} />;
}

export type ViewerMode = 'user' | 'guest';

interface ViewerProps {
  documentId: ID; draft: Draft; drafts: Draft[]; docName: string; projectId: ID; snapshotData: ComposeInput['story'] extends never ? never : import('@proofroom/schema').Snapshot;
  mode?: ViewerMode;
  onLeave?(): void;
}

function Viewer({ documentId, draft, drafts, docName, projectId, snapshotData, mode = 'user', onLeave }: ViewerProps) {
  const idx = useMemo(() => indexSnapshot(snapshotData), [snapshotData]);
  const { user, guest } = useSession();
  const isGuest = mode === 'guest';
  // a share-link guest gets the short tour; the browser remembers it for them
  const tour = useTour(isGuest ? 'guest' : 'viewer');
  const nav = useNavigate();
  const qc = useQueryClient();
  const v = useViewer();
  const { compose, cancel: cancelCompose } = useCompose(40);
  const editorRef = useRef<Editor | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const viewport = useViewportSize(canvasRef);
  const [engineOn, setEngineOn] = useState(false);
  const [published, setPublished] = useState<Draft | null>(null);
  const [sendResult, setSendResult] = useState<SendChangesResult | null>(null);
  const [commentDraft, setCommentDraft] = useState<CommentAnchor | null>(null);
  const [commentBody, setCommentBody] = useState('');
  const [commentFile, setCommentFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const rooms = useMemo(() => new RoomRegistry(draft.id, FIXTURE_MODE), [draft.id]);
  const [roomStatus, setRoomStatus] = useState<'local' | 'connecting' | 'connected' | 'disconnected'>(FIXTURE_MODE ? 'local' : 'connecting');
  const threadsQ = useThreads(documentId, draft.id);
  // the changes list and the effective stories are user-only on the server, so a guest never asks
  const changesQ = useQuery({ queryKey: keys.changes(documentId), queryFn: () => docApi.changes(documentId), enabled: !isGuest });
  const threads = useMemo(() => threadsQ.data ?? [], [threadsQ.data]);
  const changes = useMemo(() => changesQ.data ?? [], [changesQ.data]);

  /**
   * The draft's text with the designer's accepted decisions folded in, refetched whenever one
   * lands. Accepting writes nothing into the snapshot, which only moves when InDesign
   * republishes, so the stories that differ are composed here to stand in until it does.
   */
  const effectiveQ = useQuery({ queryKey: keys.effectiveStories(draft.id), queryFn: () => draftApi.effectiveStories(draft.id), enabled: !isGuest });
  const accepted = useMemo(() => {
    const out: Record<ID, Story> = {};
    for (const s of differingStories(snapshotData.stories, effectiveQ.data ?? [])) out[s.id] = s;
    return out;
  }, [snapshotData.stories, effectiveQ.data]);

  // reset viewer state for this draft and pick the creative options visible in the snapshot
  const groups = useMemo(() => creativeOptionGroups(snapshotData), [snapshotData]);
  useEffect(() => {
    v.reset(draft.id);
    let o: LayerOverrides = {};
    for (const g of groups) o = chooseOption(g, g.defaultLayerId, o);
    v.setLayerOverrides(o);
    // a guest's only panel is the threads panel
    if (isGuest) v.setPanel('threads');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id, groups]);
  useEffect(() => () => rooms.destroyAll(), [rooms]);

  // No SSE for a guest: the document (a new draft moves the link) and then the threads are
  // asked for again every GUEST_POLL_MS while the tab is visible. The document first, so a
  // draft that has moved on remounts the viewer before the threads of the old one are refetched;
  // exact, because the threads key sits under the document's and a prefix match would already
  // have refetched them alongside it, and then once more here.
  useEffect(() => {
    if (!isGuest) return;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      void qc.invalidateQueries({ queryKey: keys.document(documentId), exact: true })
        .then(() => qc.invalidateQueries({ queryKey: keys.threads(documentId, draft.id) }));
    };
    const timer = setInterval(tick, GUEST_POLL_MS);
    return () => clearInterval(timer);
  }, [isGuest, documentId, draft.id, qc]);

  const [faces, setFaces] = useState<FaceLoadResult | null>(null);
  const fontsReady = faces !== null;
  useEffect(() => {
    let alive = true;
    setFaces(null);
    registerSnapshotFaces(snapshotData.fonts).then((r) => { if (alive) setFaces(r); });
    return () => { alive = false; };
  }, [snapshotData]);
  // skipped faces (available but with no url, from an older draft) lock stories too, so name them here as well
  const unavailableFonts = useMemo(
    () => snapshotData.fonts.filter((f) => f.status === 'missing' || (faces?.failed.includes(f.id) ?? false) || (faces?.skipped.includes(f.id) ?? false)).map((f) => f.id),
    [snapshotData.fonts, faces],
  );
  // the candidate list for resolveFace: a face the draft declares missing is never a run override
  const availableFonts = useMemo(() => snapshotData.fonts.filter(isAvailableFace), [snapshotData.fonts]);
  const measure = useMemo(() => (fontsReady ? domMeasure() : undefined), [fontsReady]);
  const fontLockMap = useMemo(() => (faces ? fontLocks(idx, new Set(faces.loaded)) : {}), [idx, faces]);

  const spread = snapshotData.spreads[Math.min(v.spreadIndex, snapshotData.spreads.length - 1)];
  const scale = useMemo(() => {
    if (!spread || viewport.w === 0) return 1;
    if (v.zoom === 'fit') return fitScale(spread.bounds, viewport);
    if (v.zoom === 'fitWidth') return fitWidthScale(spread.bounds, viewport);
    return v.zoom;
  }, [spread, viewport, v.zoom]);

  useEffect(() => { flushFidelityLog(`spread ${spread?.index ?? 0}`); });

  const composeInputFor = useCallback((story: Story): ComposeInput => ({
    story,
    paragraphStyles: idx.paraStyles,
    characterStyles: idx.charStyles,
    fonts: availableFonts,
    frames: storyGeometry(idx.stories[story.id] ?? story, idx),
    baselineGrid: snapshotData.baselineGrid,
  }), [idx, snapshotData, availableFonts]);

  // overset per composed story, and the spreads it touches, for the navigator
  const overset = useMemo(() => {
    const out: Record<ID, OversetInfo> = {};
    for (const [storyId, output] of Object.entries(v.composed)) {
      const story = idx.stories[storyId];
      if (!story) continue;
      const frames = storyGeometry(story, idx);
      const at = output.oversetAt ? idx.paragraphs[output.oversetAt.paraId]?.paragraph : undefined;
      const para = at ?? story.paragraphs[story.paragraphs.length - 1];
      out[storyId] = oversetInfo(output, frames, para ? resolveParaAttrs(para, idx) : { leading: 'auto', autoLeading: 120, pointSize: 10 });
    }
    return out;
  }, [v.composed, idx]);
  const oversetSpreads = useMemo(() => {
    const s = new Set<number>();
    for (const info of Object.values(overset)) if (info.overset && info.lastFrameId) s.add(idx.itemSpread[info.lastFrameId]);
    return s;
  }, [overset, idx]);

  // editing
  const selectedStory = v.selectedStoryId ? idx.stories[v.selectedStoryId] : undefined;
  const room = useMemo(() => (selectedStory ? rooms.get(selectedStory.id) : undefined), [rooms, selectedStory]);
  useEffect(() => {
    if (!room) return;
    setRoomStatus(room.status);
    return room.onStatus(setRoomStatus);
  }, [room]);

  // TipTap fires onUpdate synchronously while the collaboration plugin applies the room's first
  // state, which is during a render, so every store write from here is deferred a microtask.
  const onEditorChange = useCallback((docJson: PMNode) => {
    if (!selectedStory) return;
    const story = materialise(docJson, selectedStory.id, selectedStory.frameIds, { paraStyles: idx.paraStyles, charStyles: idx.charStyles, fonts: availableFonts, base: baseParagraphs(selectedStory) });
    queueMicrotask(() => {
      const edited = storiesDiffer(story, selectedStory);
      if (edited) v.markDirty(selectedStory.id); else v.unmarkDirty(selectedStory.id);
      // the first update fires as the story opens, before anything is typed: InDesign's own lines
      // stand until the text differs from them, or the engine preview asks for the engine's
      if (edited || engineOn) compose(selectedStory.id, composeInputFor(story));
      else if (!accepted[selectedStory.id]) {
        // back to the draft's text within the debounce (a typo and its Backspace, an undo): the
        // compose the keystroke scheduled, or already sent to the worker, must not land its lines
        cancelCompose(selectedStory.id);
        // the store itself, not this closure's snapshot of it: a result may have landed since the render
        if (useViewer.getState().composed[selectedStory.id]) v.setComposed(selectedStory.id, undefined);
      }
    });
  }, [selectedStory, idx, availableFonts, compose, cancelCompose, composeInputFor, v, engineOn, accepted]);

  const [selInfo, setSelInfo] = useState<SelectionInfo | null>(null);
  const onSelection = useCallback((info: SelectionInfo) => {
    queueMicrotask(() => {
      setSelInfo(info);
      v.setCaret(info.paraId, info.ranges);
    });
  }, [v]);

  // why a click did nothing, said on the canvas rather than only in the console
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const showNotice = useCallback((message: string) => {
    clearTimeout(noticeTimer.current);
    setNotice(message);
    noticeTimer.current = setTimeout(() => setNotice(null), 3000);
  }, []);
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const jumpToFrame = useCallback((frameId: ID) => {
    const si = idx.itemSpread[frameId];
    if (si !== undefined && si !== v.spreadIndex) v.setSpread(si);
  }, [idx, v]);

  const onFrameClick = useCallback((frame: TextFrameItem) => {
    // a guest reads and comments; opening a story would join its editing room, which the server refuses them anyway
    if (isGuest) { showNotice('Guests can comment but not edit: use the Comment tool'); return; }
    // fontLockMap is empty until the faces have loaded, so a story could otherwise open before its lock state is known
    if (faces === null) { showNotice('Fonts are still loading, try again in a moment'); return; }
    const lock = fontLockMap[frame.storyId];
    if (lock) { showNotice(`This text uses ${lock.join(', ')}, which is not available; ask the designer to publish with it installed`); return; }
    if (v.selectedStoryId === frame.storyId) return;
    v.selectStory(frame.storyId);
  }, [v, faces, fontLockMap, showNotice, isGuest]);

  // engine preview of every story on the spread
  useEffect(() => {
    if (!spread) return;
    const storyIds = idx.spreadStories[spread.index] ?? [];
    if (engineOn) {
      for (const id of storyIds) {
        if (v.dirty[id]) continue;
        // the accepted text is the base wherever the snapshot's own would be used
        const story = accepted[id] ?? idx.stories[id];
        if (story && !story.hasTable) compose(id, composeInputFor(story));
      }
    } else {
      // an accepted edit is composed whether the engine preview is on or not
      for (const id of storyIds) if (!v.dirty[id] && !accepted[id] && v.composed[id]) v.setComposed(id, undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineOn, spread?.index]);

  // the accepted text, composed. A story the client is editing keeps the room's own text: that is
  // the paragraph under their cursor, and it is already composed from every keystroke. v.dirty is a
  // dependency so a story accepted while it was being edited composes once the edits are sent.
  useEffect(() => {
    if (!spread) return;
    for (const id of idx.spreadStories[spread.index] ?? []) {
      const story = accepted[id];
      if (!story || v.dirty[id] || idx.stories[id]?.hasTable) continue;
      compose(id, composeInputFor(story));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accepted, spread?.index, v.dirty]);

  // comments
  /** Trails createThread: the route needs a threadId, so the file cannot go up with the same request. */
  const uploadAttachment = useMutation({
    mutationFn: (args: { threadId: ID; file: File }) => threadApi.uploadAttachment(documentId, args.threadId, args.file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.threads(documentId, draft.id) });
      setCommentDraft(null); setCommentBody(''); setCommentFile(null);
    },
    // deliberately no onError beyond React Query's own isError/error: the thread already posted
    // and stays: only the composer closes itself, so the error line below has something to sit on
  });
  const createThread = useMutation({
    mutationFn: (args: { anchor: Parameters<typeof docApi.createThread>[2]; body: string }) => docApi.createThread(documentId, draft.id, args.anchor, args.body),
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: keys.threads(documentId, draft.id) });
      if (t.anchor.storyId && editorRef.current) editorRef.current.chain().setComment(t.id).run();
      v.setActiveThread(t.id);
      v.setTool('select');
      if (commentFile) uploadAttachment.mutate({ threadId: t.id, file: commentFile });
      else { setCommentDraft(null); setCommentBody(''); setCommentFile(null); }
    },
  });
  /** Both comment kinds open the same composer in the threads panel: no browser prompts. */
  const startComment = (anchor: CommentAnchor) => {
    setCommentDraft(anchor); setCommentBody(''); setCommentFile(null);
    createThread.reset(); uploadAttachment.reset();
    v.setPanel('threads');
  };
  const onComment = () => {
    const a = selInfo?.anchor;
    if (!a || !selectedStory) return;
    startComment({ storyId: selectedStory.id, paraId: a.paraId, start: a.start, end: a.end, quote: a.quote });
  };
  const onPin = (point: { x: number; y: number }) => {
    // disarm before the composer opens, so a second click on the canvas selects rather than re-pins
    v.setTool('select');
    const page = pageAtPoint(spread, point);
    startComment({ spreadId: spread.id, point, page: page?.number });
  };
  /** Thread panel label for a point-anchored comment: the page it landed on, or the spread if it fell in a gutter. */
  const pinLabel = useCallback((anchor: CommentAnchor, n: number): string => {
    if (anchor.page) return `Comment ${n}, page ${anchor.page}`;
    const sp = anchor.spreadId ? idx.spreads[anchor.spreadId] : undefined;
    return sp ? `Comment ${n}, Spread ${sp.index + 1}` : `Comment ${n}`;
  }, [idx]);

  const onActivateThread = (id: ID | undefined) => {
    v.setActiveThread(id);
    const t = threads.find((x) => x.id === id);
    if (!t) return;
    if (t.anchor.storyId) {
      const story = idx.stories[t.anchor.storyId];
      const first = story ? storyFrames(story, idx)[0] : undefined;
      if (first) jumpToFrame(first.id);
      if (story && !isGuest && v.selectedStoryId !== story.id) v.selectStory(story.id);
      v.setActiveThread(id);
    } else if (t.anchor.spreadId) {
      const si = snapshotData.spreads.findIndex((s) => s.id === t.anchor.spreadId);
      if (si >= 0) v.setSpread(si);
    }
  };

  // send to designer
  const send = useMutation({
    mutationFn: async () => {
      const stories: Story[] = [];
      for (const storyId of Object.keys(v.dirty)) {
        const json = rooms.json(storyId);
        const base = idx.stories[storyId];
        if (!json || !base) continue;
        stories.push(materialise(json, storyId, base.frameIds, { paraStyles: idx.paraStyles, charStyles: idx.charStyles, fonts: availableFonts, base: baseParagraphs(base) }));
      }
      if (stories.length === 0) return { created: [], conflicts: [] };
      return draftApi.sendChanges(draft.id, stories);
    },
    onSuccess: (result) => {
      // a send that resolves after this draft was switched away from (the toolbar's own drafts
      // list is not guarded) must not clobber whatever draft's dirty state and panel are now live
      if (useViewer.getState().draftId === draft.id) {
        setSendResult(result);
        v.clearDirty();
        v.setPanel('changes');
      }
      void qc.invalidateQueries({ queryKey: keys.changes(documentId) });
    },
  });

  // PDF preview: a temporary anchor triggers the browser's save-as, then the object URL is revoked
  const onPreviewExport = async (scope: PreviewScope) => {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const { blob, filename } = await draftApi.previewPdf(draft.id, { scope, spread: v.spreadIndex });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPreviewOpen(false);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'The preview could not be generated');
    } finally {
      setPreviewBusy(false);
    }
  };

  // live updates (never for a guest: the server answers their SSE with 403, and the viewer polls instead)
  const connection = useServerEvents((type, data) => {
    if (type === 'draft.published') {
      const d = data as Draft;
      if (d?.documentId === documentId && d.id !== draft.id) { setPublished(d); void qc.invalidateQueries({ queryKey: keys.document(documentId) }); }
    } else if (type === 'draft.updated') {
      // an approval or its withdrawal, on this draft or another one in the list: refetch so the
      // toolbar's badge and the drafts popover stay right for everyone watching, not just the actor
      const d = data as Draft;
      if (d?.documentId === documentId) void qc.invalidateQueries({ queryKey: keys.document(documentId) });
    } else if (type === 'change.created' || type === 'change.decided') {
      void qc.invalidateQueries({ queryKey: keys.changes(documentId) });
      // accept, reject and the plugin's own applied all arrive as change.decided
      if (type === 'change.decided') void qc.invalidateQueries({ queryKey: keys.effectiveStories(draft.id) });
    } else if (type === 'thread.created' || type === 'thread.updated' || type === 'comment.created') {
      void qc.invalidateQueries({ queryKey: keys.threads(documentId, draft.id) });
    }
  }, !isGuest);

  // keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('.tiptap, input, textarea, select')) { if (e.key === 'Escape') { v.selectStory(undefined); (t as HTMLElement).blur(); } return; }
      if (e.key === 'ArrowRight' || e.key === 'PageDown') v.setSpread(Math.min(snapshotData.spreads.length - 1, v.spreadIndex + 1));
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') v.setSpread(Math.max(0, v.spreadIndex - 1));
      else if (e.key === '0') v.setZoom('fit');
      else if (e.key === '1') v.setZoom(1);
      else if (e.key === '+' || e.key === '=') v.setZoom(Math.min(4, scale * 1.25));
      else if (e.key === '-') v.setZoom(Math.max(0.1, scale / 1.25));
      else if (e.key === 'p' || e.key === 'P') v.setTool(v.tool === 'pin' ? 'select' : 'pin');
      else if (e.key === 'Escape') { v.setTool('select'); v.selectStory(undefined); v.setActiveThread(undefined); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [v, scale, snapshotData.spreads.length]);

  const storyLabel = useCallback((storyId: ID | undefined): string => {
    const s = storyId ? idx.stories[storyId] : undefined;
    const text = s?.paragraphs.find((p) => p.text.trim())?.text ?? '';
    return text ? `“${text.slice(0, 32)}${text.length > 32 ? '…' : ''}”` : `Story ${storyId ?? ''}`;
  }, [idx]);
  const paraLabel = useCallback((paraId: ID): string => storyLabel(idx.paragraphs[paraId]?.storyId), [idx, storyLabel]);

  const caretStyleName = v.caretParaId ? idx.paraStyles[idx.paragraphs[v.caretParaId]?.paragraph.styleId ?? '']?.name : undefined;
  const caretFont = v.caretParaId ? resolveParaAttrs(idx.paragraphs[v.caretParaId]?.paragraph ?? { styleId: '' }, idx).fontId : undefined;
  const servedFonts = useMemo(() => snapshotData.fonts.filter((f) => f.status !== 'missing' && (faces?.loaded.includes(f.id) ?? false)), [snapshotData.fonts, faces]);
  const canBold = !!caretFont && !!resolveFace(servedFonts, caretFont, { bold: true });
  const canItalic = !!caretFont && !!resolveFace(servedFonts, caretFont, { italic: true });
  const dirtyCount = Object.keys(v.dirty).length;
  const openThreads = threads.filter((t) => t.status === 'open').length;
  const proposed = changes.filter((c) => c.status === 'proposed').length;

  if (!spread) return <Shell title={docName}><ErrorState title="This draft has no spreads" /></Shell>;

  return (
    <div className="app">
      {isGuest
        ? <GuestHeader docName={docName} guestName={guest?.name ?? 'Guest'} onLeave={() => onLeave?.()} onTour={tour.start} />
        : <Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: 'Documents', to: `/projects/${projectId}` }, { label: docName, to: `/documents/${documentId}` }]} right={<a className="btn sm ghost" href={`/documents/${documentId}/audit`} onClick={(e) => { e.preventDefault(); nav(`/documents/${documentId}/audit`); }}>Audit</a>} onTour={tour.start} />}
      {published && !isGuest && (
        <PublishBanner
          draft={published} documentId={documentId} currentDraftNumber={draft.number} dirtyCount={dirtyCount}
          sending={send.isPending} onSend={() => send.mutateAsync()} paraLabel={paraLabel} onDismiss={() => setPublished(null)}
        />
      )}
      {!FIXTURE_MODE && !isGuest && <ConnectionBanner events={connection} room={selectedStory ? roomStatus : undefined} onRetry={() => window.location.reload()} />}
      <div className="viewer">
        <Toolbar mode={mode} scale={scale} groups={groups} drafts={drafts} draft={draft} documentId={documentId} connection={connection} engineOn={engineOn} onEngineToggle={() => setEngineOn((o) => !o)} dirtyCount={dirtyCount} sending={send.isPending} onSend={() => send.mutate()} onPreview={() => { setPreviewError(null); setPreviewOpen(true); }} />
        <Navigator idx={idx} current={spread.index} oversetSpreads={oversetSpreads} onSelect={(i) => v.setSpread(i)} />
        <div className="canvas-wrap" ref={canvasRef}>
          <div className="canvas-inner">
            <SpreadSvg
              idx={idx} spread={spread} scale={scale} layerOverrides={v.layerOverrides} composed={v.composed} overset={overset}
              selectedStoryId={v.selectedStoryId} caretParaId={v.caretParaId} selection={v.selection}
              threads={threads} activeThreadId={v.activeThreadId}
              showReference={v.showReference} referenceOpacity={v.referenceOpacity} showFrames={v.showFrames} pinTool={v.tool === 'pin'}
              measure={measure} textReady={fontsReady} fontLocks={fontLockMap}
              onFrameClick={onFrameClick} onPin={onPin} onMarkerClick={onActivateThread}
            />
          </div>
          <div className="canvas-overlay">
            {!fontsReady && <span className="badge muted"><span className="spinner" style={{ width: 10, height: 10 }} />Loading fonts</span>}
            {unavailableFonts.length > 0 && <span className="badge danger" title={unavailableFonts.join(', ')}>Fonts not available: {unavailableFonts.join(', ')}</span>}
            {v.tool === 'pin' && <span className="badge">Click anywhere on the page to leave a comment</span>}
            {selectedStory && overset[selectedStory.id]?.overset && <span className="badge danger">Overset: {overset[selectedStory.id].lines} line{overset[selectedStory.id].lines === 1 ? '' : 's'} do not fit</span>}
            {send.isError && <span className="badge danger">Send failed: {(send.error as Error).message}</span>}
            {notice && <span className="badge">{notice}</span>}
          </div>
        </div>
        <aside className="side" data-tour="editor">
          <div className="tabs" role="tablist">
            {!isGuest && <button role="tab" className={v.panel === 'editor' ? 'on' : ''} onClick={() => v.setPanel('editor')}>Edit</button>}
            <button role="tab" className={v.panel === 'threads' ? 'on' : ''} onClick={() => v.setPanel('threads')} data-tour="threads">Threads {openThreads > 0 && <span className="count">{openThreads}</span>}</button>
            {!isGuest && <button role="tab" className={v.panel === 'changes' ? 'on' : ''} onClick={() => v.setPanel('changes')} data-tour="changes">Changes {proposed > 0 && <span className="count">{proposed}</span>}</button>}
          </div>
          <div className="body">
            {v.panel === 'editor' && !isGuest && (selectedStory && room ? (
              <EditorPanel
                story={selectedStory} idx={idx} room={room} threads={threads} activeThreadId={v.activeThreadId} editorRef={editorRef}
                onChange={onEditorChange} onSelection={onSelection} onThreadClick={(id) => v.setActiveThread(id)}
                caretStyleName={caretStyleName} composing={!!v.composing[selectedStory.id]} composeError={v.composeErrors[selectedStory.id]} roomStatus={roomStatus}
                canComment={!!selInfo?.anchor} canBold={canBold} canItalic={canItalic} onComment={onComment} onClose={() => v.selectStory(undefined)}
              />
            ) : (
              <div className="state"><div className="glyph">T</div><div className="title">Click a text frame to edit it</div><div className="small">Your edits reflow live in the layout. Nothing reaches the designer until you press Send.</div>{user?.role === 'client' && <div className="small muted">Signed in as a client: you can edit, comment and send changes.</div>}</div>
            ))}
            {v.panel === 'threads' && (
              <>
                {commentDraft && (
                  <div className="compose-box">
                    <div className="label">
                      {commentDraft.point
                        ? `New comment at ${Math.round(commentDraft.point.x)}, ${Math.round(commentDraft.point.y)} pt`
                        : 'New comment on the selected text'}
                    </div>
                    {commentDraft.quote && <div className="quote">“{commentDraft.quote.slice(0, 160)}{commentDraft.quote.length > 160 ? '…' : ''}”</div>}
                    <textarea
                      className="input" autoFocus value={commentBody} onChange={(e) => setCommentBody(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && commentBody.trim()) createThread.mutate({ anchor: commentDraft, body: commentBody.trim() }); }}
                      placeholder={commentDraft.point ? 'What should the designer look at here?' : 'What should change about this text?'}
                    />
                    <div className="btn-row" style={{ marginTop: 6 }}>
                      <button
                        className="btn sm primary" disabled={!commentBody.trim() || createThread.isPending || createThread.isSuccess || uploadAttachment.isPending}
                        onClick={() => createThread.mutate({ anchor: commentDraft, body: commentBody.trim() })}
                      >
                        {createThread.isPending ? 'Posting' : uploadAttachment.isPending ? 'Attaching' : 'Post comment'}
                      </button>
                      <button className="btn sm ghost" onClick={() => { setCommentDraft(null); setCommentFile(null); uploadAttachment.reset(); v.setTool('select'); }}>Cancel</button>
                      <label className="btn sm ghost">
                        {commentFile ? commentFile.name : 'Attach a file'}
                        <input
                          type="file" accept="image/png,image/jpeg,image/gif,image/webp,application/pdf" style={{ display: 'none' }}
                          onChange={(e) => setCommentFile(e.target.files?.[0] ?? null)}
                        />
                      </label>
                      {createThread.isError && <span className="error-text">Could not create the thread</span>}
                      {uploadAttachment.isError && <span className="error-text">Attachment not saved: {(uploadAttachment.error as Error)?.message ?? 'unknown error'}</span>}
                    </div>
                  </div>
                )}
                {threadsQ.isPending ? <Spinner label="Loading threads" /> : threadsQ.isError ? <ErrorState title="Could not load threads" error={threadsQ.error} retry={() => void threadsQ.refetch()} /> : (
                  <ThreadsPanel mode={mode} documentId={documentId} draftId={draft.id} threads={threads} activeThreadId={v.activeThreadId} onActivate={onActivateThread} storyLabel={storyLabel} pinLabel={pinLabel} user={user} />
                )}
              </>
            )}
            {v.panel === 'changes' && !isGuest && (
              <>
                {sendResult && (
                  <SendResultBanner created={sendResult.created} conflicts={sendResult.conflicts} paraLabel={paraLabel} onDismiss={() => setSendResult(null)} />
                )}
                {changesQ.isPending ? <Spinner label="Loading changes" /> : changesQ.isError ? <ErrorState title="Could not load changes" error={changesQ.error} retry={() => void changesQ.refetch()} /> : (
                  <ChangesPanel documentId={documentId} changes={changes} storyLabel={storyLabel} onJump={(c) => { const s = idx.stories[c.storyId]; const f = s ? storyFrames(s, idx)[0] : undefined; if (f) jumpToFrame(f.id); if (s) v.selectStory(s.id); }} />
                )}
              </>
            )}
          </div>
          {dirtyCount > 0 && <div className="foot small muted">{dirtyCount} edited stor{dirtyCount === 1 ? 'y' : 'ies'} not yet sent.</div>}
        </aside>
      </div>
      {!isGuest && (
        <PreviewDialog
          open={previewOpen} spreadIndex={v.spreadIndex} spreadCount={snapshotData.spreads.length}
          busy={previewBusy} error={previewError}
          onExport={onPreviewExport}
          onClose={() => { if (previewBusy) return; setPreviewOpen(false); setPreviewError(null); }}
        />
      )}
      <Tour id={isGuest ? 'guest' : 'viewer'} steps={isGuest ? GUEST_TOUR : VIEWER_TOUR} open={tour.open} onClose={tour.close} />
    </div>
  );
}
